import React, { useEffect, useState } from 'react';
import { getLOProgress, LOProgressResponse, LOProgress } from '../../services/api';

interface LOProgressCardProps {
  studentId: string;
}

const STATUS_COLORS: Record<string, string> = {
  mastered: '#22c55e',
  proficient: '#3b82f6',
  learning: '#eab308',
  struggling: '#ef4444',
  not_started: '#9ca3af',
};

const STATUS_LABELS: Record<string, string> = {
  mastered: 'Mastered',
  proficient: 'Proficient',
  learning: 'Learning',
  struggling: 'Needs Work',
  not_started: 'Not Started',
};

export const LOProgressCard: React.FC<LOProgressCardProps> = ({ studentId }) => {
  const [data, setData] = useState<LOProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedUnit, setExpandedUnit] = useState<number | null>(null);

  useEffect(() => {
    getLOProgress(studentId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) {
    return <div className="lo-progress-card loading">Loading...</div>;
  }

  if (!data) {
    return null;
  }

  const { summary, by_unit } = data;

  return (
    <div className="lo-progress-card">
      <h3>Learning Objectives</h3>

      <div className="lo-summary">
        <div className="summary-item mastered">
          <span className="count">{summary.mastered}</span>
          <span className="label">Mastered</span>
        </div>
        <div className="summary-item proficient">
          <span className="count">{summary.proficient}</span>
          <span className="label">Proficient</span>
        </div>
        <div className="summary-item learning">
          <span className="count">{summary.learning}</span>
          <span className="label">Learning</span>
        </div>
        <div className="summary-item struggling">
          <span className="count">{summary.struggling}</span>
          <span className="label">Needs Work</span>
        </div>
      </div>

      <div className="lo-units">
        {by_unit.map(unit => (
          <div key={unit.unit} className="lo-unit">
            <button
              className="unit-header-btn"
              onClick={() => setExpandedUnit(expandedUnit === unit.unit ? null : unit.unit)}
            >
              <span className="unit-name">Unit {unit.unit}: {unit.name}</span>
              <span className="unit-stats">
                {unit.learning_objectives.filter(lo => lo.status === 'mastered' || lo.status === 'proficient').length}
                /{unit.learning_objectives.length} proficient
              </span>
              <span className="expand-icon">{expandedUnit === unit.unit ? '▲' : '▼'}</span>
            </button>

            {expandedUnit === unit.unit && (
              <div className="lo-list">
                {unit.learning_objectives.map(lo => (
                  <LOItem key={lo.code} lo={lo} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const LOItem: React.FC<{ lo: LOProgress }> = ({ lo }) => {
  return (
    <div className={`lo-item ${lo.status}`}>
      <div className="lo-header">
        <span className="lo-code">{lo.description || lo.code}</span>
        <span
          className="lo-status"
          style={{ backgroundColor: STATUS_COLORS[lo.status] }}
        >
          {STATUS_LABELS[lo.status]}
        </span>
      </div>
      <div className="lo-stats">
        {lo.attempts > 0 ? (
          <>
            <div className="lo-accuracy-bar">
              <div
                className="lo-accuracy-fill"
                style={{
                  width: `${lo.accuracy}%`,
                  backgroundColor: STATUS_COLORS[lo.status],
                }}
              />
            </div>
            <span className="lo-accuracy-text">
              {lo.accuracy}% ({lo.correct}/{lo.attempts})
            </span>
          </>
        ) : (
          <span className="lo-not-attempted">Not attempted yet</span>
        )}
      </div>
    </div>
  );
};

export default LOProgressCard;
